/**
 * @author Gedeon NdundeCode
 * @date 13/01/2024
 */
import React, { useState, useEffect } from "react";
import useGeolocation from "react-hook-geolocation";
import { connect, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { apis as api } from "../../services/api.action";
import { cnt } from "../../services/constant.action";
import { BOM } from "../../services/defined.action";
import appLabel from "../../config/appLabel";
import { BgImgFac, HandleReqFldMsg } from "../../components/layouts/Facility";
import history from "../../history";
import AOS from "aos";
import Privacy from "./Privacy";
import appStatic from "../../config/appStaticData";

const ProtectedPrivacy = (props) => {
  /** INITIALIZE VARIABLES */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();

  const { userData, isSignedIn, location } = props;
  const [passwordShown, setPasswordShown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [switchBlock, setSwitchBlock] = useState(isSignedIn); // Controls whether to show login or privacy
  const [isUserLogin, setIsUserLogin] = useState(isSignedIn); // Tracks if user has successfully logged in in this session
  const { privacyPolicyDocumentData } = appStatic;

  // Use state variables for currentUserRole and documentsToShow
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [documentsToShow, setDocumentsToShow] = useState([]);

  // A dedicated function to handle the filtering logic
  const filterDocuments = (role, loggedInStatus) => {
    console.log("[ProtectedPrivacy] Filtering documents for role:", role, "and login status:", loggedInStatus);
    let filteredDocs = privacyPolicyDocumentData.filter(item => {
      const hasNoRole = !item.role; // Documents with no specific role are visible to all
      const roleMatches = item.role === role; // Documents matching the user's role
      return hasNoRole || roleMatches;
    });

    // Dynamically update icons based on login status
    if (loggedInStatus) {
      filteredDocs = filteredDocs.map(item => ({
        ...item,
        icon: item.isProtected ? 'fa fa-unlock text-navy' : item.icon // Unlock protected icons
      }));
    } else {
      filteredDocs = filteredDocs.map(item => ({
        ...item,
        icon: item.isProtected ? 'fa fa-lock' : item.icon // Ensure protected icons are locked
      }));
    }

    setDocumentsToShow(filteredDocs);
    console.log("[ProtectedPrivacy] Documents to show after filtering:", filteredDocs.map(doc => ({ name: doc.name, icon: doc.icon })));
  };


  // Handles switching between the login form and the privacy document view
  const onSwitchModule = (itemObj) => {
    // If the item is protected and the user is not logged in, show an alert
    if (itemObj?.isProtected && !isUserLogin) {
      BOM.AlertMsg(cnt.WARNING, "Please enter your login information to view the protected content");
    } else {
      // Otherwise, toggle the switchBlock to show/hide the privacy content
      setSwitchBlock(!switchBlock);
      console.log("[ProtectedPrivacy] Toggled switchBlock via onSwitchModule. New state:", !switchBlock);
    }
  };

  useEffect(() => {
    AOS.init({
      easing: "ease-out-back",
      duration: 1000,
      disable: function () {
        var maxWidth = 1280;
        return window.innerWidth < maxWidth;
      },
    });

    console.log("--- ProtectedPrivacy Initial/Redux Update Effect ---");
    console.log("[ProtectedPrivacy] Effect running due to userData or isSignedIn change.");
    console.log("[ProtectedPrivacy] isSignedIn (from Redux prop):", isSignedIn);
    console.log("[ProtectedPrivacy] userData (from Redux prop):", userData);
    console.log("[ProtectedPrivacy] Current switchBlock state:", switchBlock);
    console.log("[ProtectedPrivacy] Current isUserLogin state:", isUserLogin);

    // Update internal states based on Redux props if they've changed
    if (isSignedIn !== isUserLogin) {
      setIsUserLogin(isSignedIn);
      setSwitchBlock(isSignedIn); // Ensure switchBlock aligns with Redux isSignedIn
      console.log("[ProtectedPrivacy] Updated isUserLogin and switchBlock based on Redux isSignedIn:", isSignedIn);
    }


    // Set current user role for document filtering
    let roleToFilter = "Buyer"; // Default role
    if (userData && userData.companyTypeName) {
      roleToFilter = userData.companyTypeName;
      setCurrentUserRole(roleToFilter);
      console.log("[ProtectedPrivacy] Current user role set to (from userData):", roleToFilter);
    } else {
      setCurrentUserRole(roleToFilter); // Set default role
      console.log("[ProtectedPrivacy] Current user role defaulted to: Buyer (userData not available)");
    }

    // Call filterDocuments here to ensure initial documents are loaded and icons updated
    filterDocuments(roleToFilter, isSignedIn);

    console.log("--------------------------------------------------");
  }, [userData, isSignedIn]); // Re-run this effect when Redux userData or isSignedIn changes

  // Scroll-to-top button logic
  window.onscroll = function () {
    var backToTo = document.querySelector(".fEx-scroll-top");
    if (
      document.body.scrollTop > 100 ||
      document.documentElement.scrollTop > 0
    ) {
      if (backToTo) backToTo.style.display = "flex";
    } else {
      if (backToTo) backToTo.style.display = "none";
    }
  };

  // Password visibility toggle
  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  /** SUBMISSION - User Login */
  const onSignIn = (formData) => {
    setIsLoading(true);
    console.log("[ProtectedPrivacy] Attempting sign-in for:", formData.userName);

    const body = {
      userName: formData.userName,
      userPass: formData.userPass,
      sourceIp: "197.184.164.52", // Mock IP for demonstration
      browser: BOM.GetBrowserName(),
      host: window.location.host,
      osName: "windows",
      longitude: "-25.757", // Mock longitude for demonstration
      latitude: "28.1443", // Mock latitude for demonstration
      channel: "self service",
    };

    BOM.SessionLessReqAction(body, api.Authentication, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err);
        setIsLoading(false);
        console.error("[ProtectedPrivacy] Sign-in failed:", err);
      } else {
        // Fetch dynamic menu for the logged-in user
        getRoleMenu(res.userData);

        // Dispatch SIGN_IN action to update Redux state
        dispatch({
          type: cnt.SIGN_IN,
          payload: res,
        });

        // Navigate to the protected document page
        history.push("protected-privacy-document");

        // Update local component states
        setIsUserLogin(true);
        setSwitchBlock(true); // This is the crucial line that switches to displaying the document
        setIsLoading(false);

        // Re-filter documents to update icons and ensure correct display after login
        filterDocuments(res.userData.companyTypeName, true); // Pass true for loggedInStatus

        // LOG: Confirm state changes after successful login
        console.log("[ProtectedPrivacy] Sign-in successful!");
        console.log("[ProtectedPrivacy] isUserLogin set to (after sign-in API call):", true);
        console.log("[ProtectedPrivacy] switchBlock set to (after sign-in API call):", true);
      }
    });
  };

  // Fetches user role menu after successful login
  async function getRoleMenu(user) {
    console.log("[ProtectedPrivacy] Fetching role menu for user:", user.userName);

    let body = {
      profileId: user.profileId,
      roleId: user.userRoleId,
      session: user.session,
    };

    BOM.FetchReqAction(body, api.GetRoleMenu, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err);
        console.error("[ProtectedPrivacy] Failed to get role menu:", err);
      } else {
        const objList = res.roleData.FUNCTIONS;
        dispatch({
          type: cnt.MENU_DATA,
          payload: objList,
        });
        console.log("[ProtectedPrivacy] Role menu fetched and dispatched. Functions:", objList.length);
      }
    });
  }

  // --- Final Render Check ---
  console.log("--- ProtectedPrivacy Rendering Cycle ---");
  console.log("[ProtectedPrivacy] Final switchBlock state before render:", switchBlock);
  console.log("[ProtectedPrivacy] Final isUserLogin state before render:", isUserLogin);
  console.log("[ProtectedPrivacy] Final documentsToShow count before render:", documentsToShow.length);
  console.log("---------------------------------------");


  return (
    <>
      {switchBlock ? ( // If switchBlock is true, show the Privacy component (the document)
        <Privacy
          onSwitchModule={onSwitchModule}
          isUserLogin={isUserLogin}
          userData={userData}
          documentsToShow={documentsToShow} // Pass the filtered and icon-updated documents as a prop
        />
      ) : ( // Otherwise, show the login form
        <div className="fEx-innovative-platform">
          <div className="fEx-container">
            <div className="fEx-row">
              <div className="fEx-col-6">
                <div className="fEx-Innovative-img" data-aos="fade-up">
                  <form onSubmit={handleSubmit(onSignIn)} autoComplete="off">
                    <div className="fEx-fild-protected-row">
                      <label htmlFor="user">{appLabel.usernameOrEmail}</label>
                      <input
                        type="text"
                        className="fEx-from-input fEx-zero-input-focus"
                        placeholder={appLabel.usernameOrEmail}
                        {...register("userName", {
                          required: HandleReqFldMsg(appLabel.usernameOrEmail),
                          pattern: {
                            value: /^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/,
                            message: "Email must be a valid",
                          },
                        })}
                      />
                      <span className="asterisks-landing">
                        {errors?.userName?.message}
                      </span>
                    </div>
                    <div className="fEx-fild-protected-row">
                      <label htmlFor="password" className="protected">
                        {appLabel.password}
                      </label>
                      <input
                        type={passwordShown ? "text" : "password"}
                        className="fEx-from-input fEx-zero-input-focus"
                        placeholder={appLabel.password}
                        {...register("userPass", {
                          required: HandleReqFldMsg(appLabel.password),
                          minLength: {
                            value: 8,
                            message: "Minimum password length is 8 characters",
                          },
                        })}
                      />
                      <span
                        className="p-viewer"
                        onClick={() => togglePassword()}
                      >
                        <i
                          className={
                            passwordShown ? "fa fa-eye-slash" : "fa fa-eye"
                          }
                        ></i>
                      </span>
                      <span className="asterisks-landing">
                        {errors?.userPass?.message}
                      </span>
                    </div>
                    <div className="fEx-fild-row">
                      <label
                        className="fEx-check-box"
                        htmlFor="login-remember-me"
                      >
                        &nbsp;
                      </label>
                    </div>
                    <div className="fEx-fild-row">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className={`${
                          isLoading ? "fEx-on-click-btn-login" : "fEx-btn-login"
                        }`}
                      >
                        {isLoading ? (
                          <span>
                            <i className="fa fa-spinner fa-spin" /> &nbsp;{" "}
                            {appLabel.processing}
                          </span>
                        ) : (
                          appLabel.btnLogin
                        )}
                      </button>
                    </div>
                    <div className="fEx-fild-link">
                      <a
                        className="fEx-password"
                        onClick={() => history.push("/sign-in")}
                      >
                        <i className="fa fa-long-arrow-left" /> {appLabel.goToLogin}
                      </a>
                    </div>
                  </form>
                </div>
              </div>
              <div className="fEx-col-6">
                <div className="fEx-Innovative-info" data-aos="fade-down">
                  <p>
                    Secure, Robust and Innovative Platform for Buyers,
                    Suppliers, and Funders to authenticate and manage supply
                    chain transactions
                  </p>
                  <span className="fEx-divider" />

                  {/* This list shows available documents for the current role */}
                  {documentsToShow && documentsToShow.length > 0 && (
                    <ul className="fEx-icon-list-items">
                      {documentsToShow.map((item, index) => (
                        <li key={index}>
                          <a onClick={() => onSwitchModule(item)}>
                            <i className={item.icon} />
                            &nbsp;{item.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}

                  <p>Alternatively, please feel free to <a href="mailto:info@factorex.africa">contact us</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <a
        href="#"
        className="fEx-scroll-top"
        aria-label="Back to top"
        alt="back-to-top"
      >
        <i className="fa fa-arrow-up" />
      </a>
    </>
  );
};

const mapStateToProps = (state) => ({
  isSignedIn: state.auth.isSignedIn,
  userData: state.auth.userData,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ProtectedPrivacy);